import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { t } from '../../../../services/i18nService';
import './MicVolumeVisualizer.scss';
import { calculateAudioBarLevel } from '../../../../services/livesession/MicrophoneAudioLevelConverter';

/**
 * MicrophoneAudioLevelMeter - A React component that visualizes microphone audio levels
 *
 * This component creates a visual representation of microphone volume levels.
 * It uses the Web Audio API to analyze audio from a provided media stream
 * and updates the visualization in real-time.
 *
 * @param {Object} props - Component props
 * @param {MediaStream} props.stream - The media stream to visualize
 */
const MicrophoneAudioLevelMeter = ({ stream }) => {
    // Reference to the analyzer and animation frame
    const analyzerRef = useRef(null);
    const animationFrameRef = useRef(null);

    // State to track the current volume level (0-10)
    const [volumeLevel, setVolumeLevel] = useState(0);

    // Constants for segment configuration
    const NUM_LOW_SEGMENTS = 2;
    const NUM_NORMAL_SEGMENTS = 6;
    const NUM_HIGH_SEGMENTS = 1;
    const NUM_VERY_HIGH_SEGMENTS = 1;
    const TOTAL_SEGMENTS = 10;

    // CSS classes for different volume ranges
    const COLORS = {
        LOW: 'mic-volume-visualizer-gray',
        NORMAL: 'mic-volume-visualizer-green',
        HIGH: 'mic-volume-visualizer-red',
        VERY_HIGH: 'mic-volume-visualizer-red',
        ACTIVE: 'mic-volume-visualizer-black',
        INACTIVE: 'mic-volume-visualizer-inactive',
    };

    // Set up audio analyzer when media stream changes
    useEffect(() => {
        if (!stream) return;

        // Check if stream has audio tracks
        const audioTracks = stream.getAudioTracks();
        if (audioTracks.length === 0) return;

        // No need to clean up here as the useEffect cleanup function
        // will be called automatically when dependencies change

        // Create audio context and analyzer
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const analyzer = audioContext.createAnalyser();
        analyzer.fftSize = 256;

        // Connect media stream to analyzer
        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyzer);

        // Store analyzer reference
        analyzerRef.current = analyzer;

        // Start analyzing audio
        const analyzeAudio = () => {
            // Calculate volume level using the extracted function
            // The function now handles data array creation and frequency data retrieval internally
            const level = calculateAudioBarLevel(analyzer, TOTAL_SEGMENTS);
            // console.log('mic level %d', level);

            setVolumeLevel(level);

            // Continue analyzing
            animationFrameRef.current = requestAnimationFrame(analyzeAudio);
        };

        analyzeAudio();

        // Clean up on unmount or when media stream changes
        // eslint-disable-next-line consistent-return
        return () => {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
            if (source) source.disconnect();
            if (audioContext.state !== 'closed') audioContext.close();
        };
    }, [stream]);

    // Generate border segments with appropriate colors
    const renderBorder = () => {
        const segments = [];

        // Add low segments
        for (let i = 0; i < NUM_LOW_SEGMENTS; i++) {
            segments.push(<div key={`border-low-${i}`} className={`segment border ${COLORS.LOW}`} />);
        }

        // Add normal segments
        for (let i = 0; i < NUM_NORMAL_SEGMENTS; i++) {
            segments.push(<div key={`border-normal-${i}`} className={`segment border ${COLORS.NORMAL}`} />);
        }

        // Add high segments
        for (let i = 0; i < NUM_HIGH_SEGMENTS; i++) {
            segments.push(<div key={`border-high-${i}`} className={`segment border ${COLORS.HIGH}`} />);
        }

        // Add very high segments
        for (let i = 0; i < NUM_VERY_HIGH_SEGMENTS; i++) {
            segments.push(<div key={`border-very-high-${i}`} className={`segment border ${COLORS.VERY_HIGH}`} />);
        }

        return <div className="border-container">{segments}</div>;
    };

    // Generate core segments that change color based on volume level
    const renderCore = () => {
        const segments = [];

        for (let i = 0; i < TOTAL_SEGMENTS; i++) {
            const isActive = i < volumeLevel;
            if (isActive) {
                // Use CSS class for active segments
                segments.push(
                    <div
                        key={`core-${i}`}
                        className={`segment core ${COLORS.ACTIVE}`}
                    />
                );
            } else {
                // Use CSS class for inactive segments
                segments.push(
                    <div
                        key={`core-${i}`}
                        className={`segment core ${COLORS.INACTIVE}`}
                    />
                );
            }
        }

        return <div className="core-container">{segments}</div>;
    };

    // Don't render if there's no stream
    if (!stream) {
        return (
            <div className="mic-volume-visualizer">
                <div className="mic-volume-visualizer-empty">
                    {t('hardwareAccessCheck.noMicrophoneDetected')}
                </div>
            </div>
        );
    }

    // Check if the stream has audio tracks
    const hasAudioTracks = stream.getAudioTracks().length > 0;
    if (!hasAudioTracks) {
        return (
            <div className="mic-volume-visualizer">
                <div className="mic-volume-visualizer-empty">
                    {t('hardwareAccessCheck.noAudioTrack')}
                </div>
            </div>
        );
    }

    return (
        <div className="mic-volume-visualizer">
            {renderBorder()}
            {renderCore()}
            {renderBorder()}
        </div>
    );
};

MicrophoneAudioLevelMeter.propTypes = {
    stream: PropTypes.object,
};

export default MicrophoneAudioLevelMeter;
